<script lang="ts">
	import { Icon } from '@repo/commons/components'
	import {
		addMonths,
		generateYearRange,
		getDaysInMonth,
		getWeekdayNames,
		isInRange,
		isSameDay,
		startOfMonth
	} from './calendarUtils.ts'
	import { locale } from 'svelte-i18n'

	import { format, getDay, subMonths, setYear, getYear } from 'date-fns'

	interface Range {
		from: Date
		to?: Date
	}

	type Selection = Date | Range | undefined

	interface CalendarProps {
		mode?: 'single' | 'range'
		defaultMonth?: Date
		selected?: Selection
		onSelect?: (value: Selection) => void
	}

	let {
		mode = 'range',
		defaultMonth = new Date(),
		selected,
		onSelect = () => {}
	}: CalendarProps = $props()

	let currentMonth = $state(startOfMonth(defaultMonth))

	const currentLocale = $derived($locale)
	const weekDays = $derived.by(() => getWeekdayNames(currentLocale as string))
	const yearOptions = $derived.by(() => generateYearRange(5, 5))

	function handleDateClick(date: Date) {
		if (mode === 'single') {
			onSelect(date)
		} else {
			const range = selected as Range | undefined
			if (!range || range.to) {
				onSelect({ from: date })
			} else if (range.from && date < range.from) {
				onSelect({ from: date, to: range.from })
			} else {
				onSelect({ from: range.from, to: date })
			}
		}
	}

	function isSelected(date: Date): boolean {
		if (mode === 'single') {
			return isSameDay(date, selected as Date)
		} else {
			const range = selected as Range | undefined
			return isInRange(date, range?.from, range?.to)
		}
	}

	function getMonthMatrix(month: Date): Date[] {
		const firstDayOfMonth = startOfMonth(month)
		const days = getDaysInMonth(month)

		const startOffset = getDay(firstDayOfMonth)
		const leading = Array.from({ length: startOffset }).map((_, i) => {
			const d = new Date(month)
			d.setDate(-startOffset + i + 1)
			return d
		})

		return [...leading, ...days]
	}

	function isRangeStart(date: Date): boolean {
		if (mode !== 'range') return false
		const range = selected as Range | undefined
		return !!range?.from && isSameDay(date, range.from)
	}

	function isRangeEnd(date: Date): boolean {
		if (mode !== 'range') return false
		const range = selected as Range | undefined
		return !!range?.to && isSameDay(date, range.to)
	}

	function prevMonth() {
		currentMonth = subMonths(currentMonth, 1)
	}

	function nextMonth() {
		currentMonth = addMonths(currentMonth, 1)
	}

	function handleYearChange(event: Event) {
		const target = event.target as HTMLSelectElement
		const newYear = parseInt(target.value, 10)
		currentMonth = setYear(currentMonth, newYear)
	}
</script>

<div class="calendar">
	<div class="calendar-month">
		<!-- Navigation Header -->
		<div class="calendar-header">
			<button class="nav-button" onclick={prevMonth} aria-label="Previous month">
				<Icon name="arrowLeft" class="text-[#002C4D80]" />
			</button>

			<div class="month-year-container">
				<div class="month-label">
					{format(addMonths(currentMonth, 0), 'MMMM')}
				</div>
				<div class="year-selector-wrapper">
					<select
						class="year-selector"
						value={getYear(currentMonth)}
						onchange={handleYearChange}
						aria-label="Select year"
					>
						{#each yearOptions as year}
							<option value={year}>{year}</option>
						{/each}
					</select>
				</div>
			</div>

			<button class="nav-button" onclick={nextMonth} aria-label="Next month">
				<Icon name="arrowRight" class="text-[#002C4D80]" />
			</button>
		</div>

		<!-- Weekday labels -->
		<div class="calendar-weekdays">
			{#each weekDays as day}
				<div class="weekday">{day}</div>
			{/each}
		</div>

		<!-- Days grid -->
		<div class="calendar-days">
			{#each getMonthMatrix(addMonths(currentMonth, 0)) as day (day.toISOString())}
				<button
					class:outside-month={day.getMonth() !== addMonths(currentMonth, 0).getMonth()}
					class:selected={isSelected(day)}
					class:start={isRangeStart(day)}
					class:end={isRangeEnd(day)}
					class:isToday={isSameDay(day, new Date())}
					class="day-button"
					onclick={() => handleDateClick(day)}
				>
					{day.getDate()}
				</button>
			{/each}
		</div>
	</div>
</div>

<style>
	.calendar {
		@apply flex gap-8 p-4 bg-white rounded-lg font-sans;
	}

	.calendar-month {
		width: 100%;
	}

	.calendar-header {
		@apply flex justify-between items-center;
	}

	.month-year-container {
		@apply flex items-center gap-2 flex-1 justify-center;
	}

	.month-label {
		@apply font-semibold text-lg;
	}

	.year-selector-wrapper {
		@apply relative;
	}

	.year-selector {
		@apply appearance-none bg-transparent border-none font-semibold text-lg cursor-pointer pr-6 outline-none;
		color: inherit;
	}

	.year-selector-wrapper::after {
		content: '';
		@apply absolute top-1/2 right-0 -translate-y-1/2 w-0 h-0 pointer-events-none;
		border-left: 4px solid transparent;
		border-right: 4px solid transparent;
		border-top: 4px solid #002c4d80;
	}

	.year-selector:hover {
		@apply text-[#007ACC];
	}

	.year-selector:focus {
		@apply text-[#007ACC];
	}

	.nav-button {
		@apply h-12 w-12 flex items-center justify-center;
		border: none;
		background: none;
		cursor: pointer;
		border-radius: 4px;
		transition: background 0.2s;
	}

	.nav-button:hover {
		background: #f0f0f0;
	}

	.calendar-weekdays {
		display: grid;
		grid-template-columns: repeat(7, 1fr);
		text-align: center;
		font-size: 0.8rem;
		color: #888;
		border-bottom: 1px solid #003a661a;
		margin-bottom: 0.5rem;
		@apply flex items-center justify-center w-full min-h-12;
	}

	.weekday {
		@apply flex-1 text-[#001D33B2];
		font-weight: 400;
	}

	.calendar-days {
		display: grid;
		grid-template-columns: repeat(7, 1fr);
		row-gap: 0.5rem;
	}

	.day-button {
		@apply relative aspect-square w-full border-none bg-transparent cursor-pointer text-[0.9rem] text-[#001626E5] transition-[background,color] duration-200;
	}

	.day-button:hover {
		background-color: #0049800d;
	}

	.day-button.selected {
		@apply bg-[#007ACC] text-[#ffffff];
	}

	.day-button.selected.start {
		@apply rounded-l-lg;
	}

	.day-button.selected.end {
		@apply rounded-r-lg;
	}

	.day-button.isToday::after {
		content: '';
		@apply absolute w-1.5 h-1.5 bg-[#007ACC] rounded-full bottom-[4px] left-0 right-0 mx-auto;
	}

	.day-button.selected.isToday::after {
		content: '';
		@apply absolute w-1.5 h-1.5 bg-white rounded-full bottom-[4px] left-0 right-0 mx-auto;
	}

	.day-button.outside-month {
		@apply text-[#001D33B2];
	}
	.day-button.outside-month.selected {
		@apply text-[#ffffff]/70;
	}
</style>
